import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Globe, Sparkles, ShieldCheck } from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { PageHero, Section, SectionHeader, CTAStrip, StatStrip } from './shared/blocks';
import SEO from './shared/SEO';
import { Button } from '../../components/ui/button';
import { STATS, BRAND } from './content';

export default function AboutPage() {
  return (
    <MarketingLayout>
      <SEO
        title="About ActanonEdu | Built for Indian teachers, by Indian educators"
        description="ActanonEdu is an India-first AI teaching platform and School ERP. Built to give teachers their weekends back — board-aware, multilingual, and privacy-first."
        path="/about"
        keywords={['About ActanonEdu', 'Indian edtech', 'AI education India']}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'About', url: '/about' }]}
      />
      <PageHero
        eyebrow="About"
        title="Indian teachers deserve"
        accent="Indian-grade tools."
        subtitle="ActanonEdu is built by a small team obsessed with the modern Indian teacher — the boards they teach, the languages they speak, the boards they answer to."
        testid="about-hero"
      />

      <Section>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHeader eyebrow="Why we exist" title="The Sunday-night problem." />
            <div className="space-y-4 text-base text-mk-muted dark:text-slate-300 leading-relaxed">
              <p>
                We started with one observation: Indian teachers were spending their <strong>Sundays</strong> writing question papers — by hand, by Word, by copy-paste from a decade of saved files.
              </p>
              <p>
                The tools available either weren&apos;t board-aware (generic chatbots that hallucinate ICSE patterns), weren&apos;t multilingual (English-only, despite half the country teaching in regional languages), or weren&apos;t safe (data leaving the country, no school-grade controls).
              </p>
              <p>
                So we built one for India — board-pattern aware, multilingual in 7 Indian languages, math-validated, with a full School ERP attached. Made in India, for India.
              </p>
            </div>
          </div>
          {/* Stats grid — explicit 2-col layout so wide numbers like "50,000+"
              never get clipped on narrow viewports. */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {STATS.map((it, i) => (
              <div key={i} className="rounded-2xl bg-gradient-to-br from-mk-midnight to-mk-navy text-white px-4 sm:px-5 py-5 sm:py-6 text-center">
                <div className="text-2xl sm:text-3xl font-extrabold tracking-tight bg-gradient-to-r from-mk-cyan to-mk-cyan bg-clip-text text-transparent break-words">
                  {it.to ? (it.to.toLocaleString('en-IN') + (it.suffix || '')) : it.value}
                </div>
                <div className="text-[11px] sm:text-xs text-slate-300 mt-1.5 leading-tight">{it.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeader eyebrow="Our principles" title="What we hold ourselves to." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Heart,       title: 'Teachers first', desc: 'Every feature is shipped with a teacher in the room.' },
            { icon: Globe,       title: 'India-native',   desc: 'Boards, languages, and pedagogy that actually exist here.' },
            { icon: Sparkles,    title: 'Quality over breadth', desc: 'Six tools done excellently > sixty done averagely.' },
            { icon: ShieldCheck, title: 'Data dignity',   desc: 'Student data stays in India. We never train on it.' },
          ].map((p) => (
            <div key={p.title} className="rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-5">
              <p.icon className="h-5 w-5 text-mk-blue dark:text-mk-cyan mb-2" />
              <div className="font-bold">{p.title}</div>
              <div className="text-sm text-mk-muted dark:text-slate-300 mt-1 leading-relaxed">{p.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      <CTAStrip
        title="Want to help shape what comes next?"
        subtitle="Founder reads every WhatsApp. Your feedback ships."
        primary={{ label: 'Sign up free', href: '/signup' }}
        secondary={{ label: 'Chat with Founder', wa: 'Hi Founder! I read the About page and want to share feedback.' }}
        testid="about-final-cta"
      />
    </MarketingLayout>
  );
}
